import { gql } from "@apollo/client";

// ==========================================
// BILL QUERIES
// ==========================================

/** Get all bills for the current tenant (used in Bills list page) */
export const GET_ALL_BILLS = gql`
  query GetAllBills {
    bills {
      id
      status
      isPaid
      issuedBy
      currency
      lineItems {
        id
        description
        amount
        createdAt
        updatedAt
      }
      subtotal
      taxPercentage
      taxAmount
      totalAmount
      createdAt
      updatedAt
      tenantId
    }
  }
`;

/** Get a single bill by ID (authenticated) */
export const GET_BILL = gql`
  query GetBillById($id: ID!) {
    bill(id: $id) {
      id
      isPaid
      status
      paymentMethod
      currency
      acceptCardPayment
      acceptCryptoPayment
      acceptTokenPayment
      tokenDiscountAmount
      tokensApplied
      projectId
      clientId
      issuedBy
      lineItems {
        id
        description
        amount
        createdAt
        updatedAt
      }
      subtotal
      taxPercentage
      taxAmount
      totalAmount
      tenantId
      createdAt
      updatedAt
    }
  }
`;

/** Get a single bill by ID for payment success page (minimal fields) */
export const GET_BILL_PAYMENT_SUCCESS = gql`
  query GetBillById($id: ID!) {
    bill(id: $id) {
      id
      isPaid
      status
      paymentMethod
      currency
      projectId
      totalAmount
      lineItems {
        id
        description
        amount
      }
      createdAt
      updatedAt
    }
  }
`;

/** Public bill query (no authentication required) */
export const PUBLIC_GET_BILL = gql`
  query PublicGetBillById($id: ID!) {
    publicBill(id: $id) {
      id
      isPaid
      status
      paymentMethod
      currency
      acceptBankTransfer
      acceptCardPayment
      acceptCryptoPayment
      acceptTokenPayment
      tokenDiscountAmount
      tokensApplied
      projectId
      clientId
      issuedBy
      lineItems {
        id
        description
        amount
        createdAt
        updatedAt
      }
      subtotal
      taxPercentage
      taxAmount
      totalAmount
      tenantId
      paymentEvents {
        method
        status
        amount
        paidAt
        reference
        details
        screenshotUrl
        confirmedAt
        confirmedBy
      }
      createdAt
      updatedAt
    }
  }
`;

// ==========================================
// CLIENT QUERIES (bill-related)
// ==========================================

/** Get client details by bill ID (authenticated) */
export const GET_CLIENT_BY_BILL = gql`
  query GetClientDetailsByBillId($billId: ID!) {
    getClientDetailsByBillId(billId: $billId) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
    }
  }
`;

/** Get client details by bill ID for payment success (minimal fields) */
export const GET_CLIENT_BY_BILL_PAYMENT_SUCCESS = gql`
  query GetClientDetailsByBillId($billId: ID!) {
    getClientDetailsByBillId(billId: $billId) {
      id
      fName
      lName
      email
      businessName
    }
  }
`;

/** Public client query by bill ID (no authentication required) */
export const PUBLIC_GET_CLIENT_BY_BILL = gql`
  query PublicGetClientDetailsByBillId($billId: ID!) {
    publicGetClientDetailsByBillId(billId: $billId) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
    }
  }
`;

/** Get a single client by ID */
export const GET_CLIENT = gql`
  query GetClient($id: ID!) {
    client(id: $id) {
      id
      fName
      lName
      email
      businessName
      phoneNumber
    }
  }
`;

/** Get all clients (for NewBill client selector) */
export const GET_CLIENTS = gql`
  query GetClients {
    clients {
      id
      fName
      lName
      email
      businessName
    }
  }
`;

// ==========================================
// BILL ISSUER QUERIES
// ==========================================

/** Get bill issuer details (authenticated) */
export const GET_BILL_ISSUER = gql`
  query GetBillIssuerDetails($billId: ID!) {
    getBillIssuerDetails(billId: $billId) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
      businessRegistrationNumber
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
        acceptTokenPayment
      }
    }
  }
`;

/** Public bill issuer details (no authentication required) */
export const PUBLIC_GET_BILL_ISSUER = gql`
  query PublicGetBillIssuerDetails($billId: ID!) {
    publicGetBillIssuerDetails(billId: $billId) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
      businessRegistrationNumber
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
        acceptTokenPayment
      }
    }
  }
`;

// ==========================================
// TENANT QUERIES
// ==========================================

/** Get current tenant with company details (for NewBill tax percentage) */
export const GET_CURRENT_TENANT = gql`
  query GetCurrentTenant {
    currentTenant {
      id
      name
      companyDetails {
        companyName
        taxId
        taxPercentage
        billingEmail
        billingPhone
        billingAddress {
          addressLine1
          addressLine2
          city
          state
          postalCode
          country
        }
      }
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
        acceptTokenPayment
      }
    }
  }
`;

/** Get current tenant for CompanyDetails page (with invoice preferences) */
export const GET_CURRENT_TENANT_COMPANY = gql`
  query GetCurrentTenant {
    currentTenant {
      id
      name
      companyDetails {
        companyName
        taxId
        billingEmail
        billingPhone
        taxPercentage
        billingAddress {
          addressLine1
          addressLine2
          city
          state
          postalCode
          country
        }
        invoicePreferences {
          emailInvoices
          autoPayEnabled
          invoiceLanguage
          taxIncluded
        }
      }
    }
  }
`;

/** Get current tenant for PaymentReceivingDetails page */
export const GET_CURRENT_TENANT_PAYMENT = gql`
  query GetCurrentTenant {
    currentTenant {
      id
      name
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
        acceptTokenPayment
      }
    }
  }
`;

/** Get current tenant for EditPaymentDetails page */
export const GET_CURRENT_TENANT_EDIT_PAYMENT = gql`
  query GetCurrentTenant {
    currentTenant {
      id
      name
      companyDetails {
        companyName
        taxId
        billingEmail
        billingPhone
      }
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
        acceptTokenPayment
      }
    }
  }
`;

/** Get current tenant details for BillDetails page */
export const GET_CURRENT_TENANT_DETAILS = gql`
  query GetCurrentTenantDetails {
    currentTenant {
      id
      name
      companyDetails {
        companyName
        taxId
        billingEmail
        billingPhone
        billingAddress {
          addressLine1
          addressLine2
          city
          state
          postalCode
          country
        }
      }
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        paypalEmail
        cryptoDiscountPercentage
        acceptTokenPayment
      }
    }
  }
`;

/** Get a tenant by ID (for EditPaymentDetails Stripe config) */
export const GET_TENANT = gql`
  query GetTenant($id: ID!) {
    tenant(id: $id) {
      id
      name
      domain
      websiteUrl
      apiKeys {
        stripePublicKey
        stripeSecretKey
        stripeWebhookSecret
      }
      branding {
        siteName
      }
    }
  }
`;

/** Get tenant config for Stripe public key */
export const GET_TENANT_CONFIG = gql`
  query GetTenantConfig($id: ID!) {
    tenant(id: $id) {
      id
      apiKeys {
        stripePublicKey
      }
    }
  }
`;

// ==========================================
// PROJECT QUERIES
// ==========================================

/** Get all projects (for NewBill project selector) */
export const GET_ALL_PROJECTS = gql`
  query GetAllProjects {
    projects {
      id
      projectName
      projectGoal
      progress
      billingClient {
        id
        fName
        lName
        email
        businessName
      }
    }
  }
`;

/** Get a single project with its tasks (for NewBill line items) */
export const GET_PROJECT_WITH_TASKS = gql`
  query GetProjectWithTasks($projectId: ID!) {
    project(id: $projectId) {
      id
      projectName
      projectGoal
      progress
      billingClient {
        id
        fName
        lName
        email
        businessName
      }
      tasks {
        id
        description
        status
        order
        billed
        assignedTo {
          id
          fName
          lName
        }
        media {
          url
          description
          fileType
        }
      }
    }
  }
`;

// ==========================================
// BILL MUTATIONS
// ==========================================

/** Create a new bill */
export const CREATE_BILL = gql`
  mutation CreateBill($input: BillInput!) {
    createBill(input: $input) {
      id
      isPaid
      status
      paymentMethod
      currency
      acceptCardPayment
      acceptCryptoPayment
      clientId
      lineItems {
        id
        amount
        description
      }
      subtotal
      taxPercentage
      taxAmount
      totalAmount
      projectId
      createdAt
      updatedAt
    }
  }
`;

/** Delete a bill */
export const DELETE_BILL = gql`
  mutation DeleteBill($id: ID!) {
    deleteBill(id: $id)
  }
`;

/** Toggle bill payment status (paid/unpaid) */
export const TOGGLE_BILL_PAYMENT_STATUS = gql`
  mutation ToggleBillPaymentStatus($id: ID!) {
    toggleBillPaymentStatus(id: $id) {
      id
      isPaid
    }
  }
`;

/** Toggle bill draft status (cycles: PROPOSAL -> DRAFT -> SENT) */
export const TOGGLE_BILL_DRAFT_STATUS = gql`
  mutation ToggleBillDraftStatus($id: ID!) {
    toggleBillDraftStatus(id: $id) {
      id
      status
    }
  }
`;

/** Update bill status with email/SMS notifications */
export const UPDATE_BILL_STATUS = gql`
  mutation UpdateBillStatus($id: ID!, $status: BillStatus!) {
    updateBillStatus(id: $id, status: $status) {
      id
      status
      lineItems {
        id
        description
        amount
      }
      isPaid
      totalAmount
      paymentMethod
      createdAt
      updatedAt
    }
  }
`;

/** Update bill status without sending notifications */
export const UPDATE_BILL_STATUS_WITHOUT_NOTIFICATIONS = gql`
  mutation UpdateBillStatusWithoutNotifications($id: ID!, $status: BillStatus!, $cancellationReason: String) {
    updateBillStatusWithoutNotifications(id: $id, status: $status, cancellationReason: $cancellationReason) {
      id
      status
      cancellationReason
      cancelledAt
      lineItems {
        id
        description
        amount
      }
      isPaid
      totalAmount
      paymentMethod
      createdAt
      updatedAt
    }
  }
`;

/** Update a line item on a bill */
export const UPDATE_LINE_ITEM = gql`
  mutation UpdateLineItem($billId: ID!, $lineItemId: ID!, $amount: Float, $description: String) {
    updateLineItem(
      billId: $billId
      lineItemId: $lineItemId
      amount: $amount
      description: $description
    ) {
      id
      lineItems {
        id
        description
        amount
        createdAt
        updatedAt
      }
      status
      totalAmount
      isPaid
      updatedAt
    }
  }
`;

/** Delete a line item from a bill */
export const DELETE_LINE_ITEM = gql`
  mutation DeleteLineItem($billId: ID!, $lineItemId: ID!) {
    deleteLineItem(billId: $billId, lineItemId: $lineItemId) {
      id
      lineItems {
        id
        description
        amount
        createdAt
        updatedAt
      }
      status
      totalAmount
      isPaid
      updatedAt
    }
  }
`;

/** Update bill payment method preferences */
export const UPDATE_BILL_PAYMENT_METHODS = gql`
  mutation UpdateBill($id: ID!, $input: BillInput!) {
    updateBill(id: $id, input: $input) {
      id
      acceptBankTransfer
      acceptCardPayment
      acceptCryptoPayment
      acceptTokenPayment
    }
  }
`;

/** Duplicate a bill with a percentage of original amounts */
export const DUPLICATE_BILL_WITH_PERCENTAGE = gql`
  mutation DuplicateBillWithPercentage($id: ID!, $percentage: Float!, $paymentTerms: String) {
    duplicateBillWithPercentage(id: $id, percentage: $percentage, paymentTerms: $paymentTerms) {
      id
      isPaid
      status
      currency
      lineItems {
        id
        description
        amount
      }
      totalAmount
      percentageOfTotal
      paymentTerms
    }
  }
`;

/** Change the client associated with a bill */
export const CHANGE_BILL_CLIENT = gql`
  mutation ChangeBillClient($billId: ID!, $newClientId: ID!) {
    changeBillClient(billId: $billId, newClientId: $newClientId)
  }
`;

/** Import line items from markdown format */
export const IMPORT_LINE_ITEMS_FROM_MARKDOWN = gql`
  mutation ImportLineItemsFromMarkdown($billId: ID!, $markdown: String!) {
    importLineItemsFromMarkdown(billId: $billId, markdown: $markdown) {
      id
      lineItems {
        id
        description
        amount
      }
      totalAmount
      status
      isPaid
      updatedAt
    }
  }
`;

/** Create a bill payment via Stripe */
export const CREATE_BILL_PAYMENT = gql`
  mutation CreateBillPayment($billId: ID!, $paymentMethodId: String!) {
    createBillPayment(billId: $billId, paymentMethodId: $paymentMethodId) {
      success
      requiresAction
      clientSecret
      paymentIntentId
      message
    }
  }
`;

/** Redeem BB Tokens to pay a bill */
export const REDEEM_TOKENS_FOR_BILL = gql`
  mutation RedeemTokensForBill($billId: ID!, $tokenCount: Int!) {
    redeemTokensForBill(billId: $billId, tokenCount: $tokenCount) {
      success
      message
      tokensRedeemed
      tokenValueApplied
      remainingBalance
      billRemainingAmount
    }
  }
`;

// ==========================================
// TENANT MUTATIONS
// ==========================================

/** Update tenant payment receiving details */
export const UPDATE_TENANT_PAYMENT_DETAILS = gql`
  mutation UpdateTenantPaymentDetails($paymentDetails: PaymentReceivingDetailsInput!) {
    updateTenantPaymentDetails(paymentDetails: $paymentDetails) {
      id
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
        acceptTokenPayment
      }
    }
  }
`;

/** Update tenant company details */
export const UPDATE_TENANT_COMPANY_DETAILS = gql`
  mutation UpdateTenantCompanyDetails($companyDetails: TenantCompanyDetailsInput!) {
    updateTenantCompanyDetails(companyDetails: $companyDetails) {
      id
      companyDetails {
        companyName
        taxId
        billingEmail
        billingPhone
        taxPercentage
        billingAddress {
          addressLine1
          addressLine2
          city
          state
          postalCode
          country
        }
        invoicePreferences {
          emailInvoices
          autoPayEnabled
          invoiceLanguage
          taxIncluded
        }
      }
    }
  }
`;

/** Update tenant (for Stripe API keys) */
export const UPDATE_TENANT = gql`
  mutation UpdateTenant($id: ID!, $input: UpdateTenantInput!) {
    updateTenant(id: $id, input: $input) {
      id
      apiKeys {
        stripePublicKey
        stripeSecretKey
        stripeWebhookSecret
      }
    }
  }
`;

// ==========================================
// BANK TRANSFER PAYMENT MUTATIONS
// ==========================================

export const SUBMIT_BANK_TRANSFER_PAYMENT = gql`
  mutation SubmitBankTransferPayment($billId: ID!, $amount: Float!, $screenshotUrl: String) {
    submitBankTransferPayment(billId: $billId, amount: $amount, screenshotUrl: $screenshotUrl)
  }
`;

export const CONFIRM_BANK_TRANSFER_PAYMENT = gql`
  mutation ConfirmBankTransferPayment($billId: ID!, $paymentEventIndex: Int!) {
    confirmBankTransferPayment(billId: $billId, paymentEventIndex: $paymentEventIndex)
  }
`;

export const RESET_PAYMENT_EVENT = gql`
  mutation ResetPaymentEvent($billId: ID!, $paymentEventIndex: Int!) {
    resetPaymentEvent(billId: $billId, paymentEventIndex: $paymentEventIndex)
  }
`;

// ==========================================
// BILL NOTIFICATION RULE QUERIES / MUTATIONS
// ==========================================

const BILL_NOTIFICATION_RULE_FIELDS = `
  id
  name
  trigger
  recipientType
  recipientIds
  recipientTag
  channels
  timing
  timingMinutes
  isEnabled
  messageTemplate
  createdAt
`;

/** Get all bill notification rules */
export const GET_BILL_NOTIFICATION_RULES = gql`
  query GetBillNotificationRules {
    billNotificationRules {
      ${BILL_NOTIFICATION_RULE_FIELDS}
    }
  }
`;

/** Create a bill notification rule */
export const CREATE_BILL_NOTIFICATION_RULE = gql`
  mutation CreateBillNotificationRule($input: CreateBillNotificationRuleInput!) {
    createBillNotificationRule(input: $input) {
      ${BILL_NOTIFICATION_RULE_FIELDS}
    }
  }
`;

/** Update a bill notification rule */
export const UPDATE_BILL_NOTIFICATION_RULE = gql`
  mutation UpdateBillNotificationRule($input: UpdateBillNotificationRuleInput!) {
    updateBillNotificationRule(input: $input) {
      ${BILL_NOTIFICATION_RULE_FIELDS}
    }
  }
`;

/** Delete a bill notification rule */
export const DELETE_BILL_NOTIFICATION_RULE = gql`
  mutation DeleteBillNotificationRule($id: ID!) {
    deleteBillNotificationRule(id: $id)
  }
`;

/** Toggle a bill notification rule enabled/disabled */
export const TOGGLE_BILL_NOTIFICATION_RULE = gql`
  mutation ToggleBillNotificationRule($id: ID!) {
    toggleBillNotificationRule(id: $id) {
      ${BILL_NOTIFICATION_RULE_FIELDS}
    }
  }
`;

/** Search clients for notification rule recipients */
export const SEARCH_CLIENTS_FOR_NOTIFICATIONS = gql`
  query SearchClientsForNotifications {
    clients {
      id
      fName
      lName
      email
    }
  }
`;
